import { IUser } from '@api/models';
import http from 'httpConfig/http';
import { Label } from '@library/forms';
import { CircularProgress } from '@library/loaders/components';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { FC } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { produce } from 'immer';
import { getResellerIdFromRedux } from 'modules/shared/helpers/shared.utils';
import { Button, Drawer } from '@hyperflake/react-ui-library';
import { IProjectLink } from '@api/models/project-links.model';

interface ILinkDeleteConfirmationDrawerProps {
    open: boolean;
    onClose: () => void;
    link: IProjectLink | null;
}

const LinkDeleteConfirmationDrawer = (props: ILinkDeleteConfirmationDrawerProps) => {
    const { open, onClose, link } = props;
    const { clientId, projectId } = useParams();
    const queryClient = useQueryClient();

    const deleteLinkMutation = useMutation(async (payload: any) => {
        const { data, status } = await http.delete(
            `${
                import.meta.env.VITE_APP_API_URL
            }/${getResellerIdFromRedux()}/clients/${clientId}/projects/${projectId}/project-links/${payload.id}`
        );
        return { data, status };
    });

    const handleDelete = async () => {
        if (link === null) return onClose();
        try {
            const { data, status } = await deleteLinkMutation.mutateAsync({
                id: link._id,
            });
            console.log({ data, status });
            toast.success('Link deleted Successfully.');
            queryClient.setQueryData<IProjectLink[]>(['clients', clientId, 'projects', projectId, 'links'], (prev) => {
                if (!prev) return;

                const index = prev.findIndex((c) => c._id === link._id);
                return produce(prev, (draft) => {
                    draft.splice(index, 1);
                    return draft;
                });
            });
            onClose();
        } catch (ex: any) {
            onClose();
            console.log(ex.status);
            if (ex.status === 400) {
                toast.error(ex.data.message);
            } else {
                toast.error(ex?.data?.message || 'Some error occured, please try again.');
                console.log(ex);
            }
        }
    };

    return (
        <Drawer open={open} onClose={onClose} title="Confirm Delete">
            <div>
                <div>
                    <Label className="mt-8 inline-block">
                        The following link &ldquo;
                        <span className="font-bold">{link?.name}</span>&rdquo; will be deleted.
                    </Label>

                    <div className="flex gap-2 mt-2">
                        <Button
                            disabled={deleteLinkMutation.isLoading}
                            onClick={() => onClose()}
                            variant="default"
                            color={'primary'}
                        >
                            Cancel
                        </Button>
                        <Button
                            disabled={deleteLinkMutation.isLoading}
                            className="w-20"
                            onClick={() => handleDelete()}
                            variant="default"
                            color={'danger'}
                        >
                            {deleteLinkMutation.isLoading ? <CircularProgress size={18} /> : 'Delete'}
                        </Button>
                    </div>
                </div>
            </div>
        </Drawer>
    );
};

export default LinkDeleteConfirmationDrawer;
